import { HelpCircle } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import { useArmLabels } from '../../hooks/use-arm-labels';
import { useExperimentRoasComparison } from '../../hooks/use-experiments';
import type { RoasComparisonResponse } from '../../types/experiment.types';
import { armLetterLabelKey } from '../../utils/arm-labels';

import { ExperimentDataUnavailable } from './experiment-data-unavailable';
import { ExperimentLiftBadge } from './experiment-lift-badge';

interface Props {
  experimentId: string;
  /** Encoded `?segment=` product filter (see `encodeSegmentFilter`); `undefined` = unscoped. */
  segment?: string | undefined;
}

/** Columns before the arm groups: the metric label and the baseline. */
const LEADING_COLUMNS = 2;
/** Each test arm group spans its value column and its lift column. */
const COLUMNS_PER_TEST_ARM = 2;

/**
 * Left divider opening each arm group. Applied to the group header and to the
 * first body cell of every row, so the pair reads as one block down the table.
 */
const GROUP_START = 'border-l pl-4';

export function ExperimentRoasComparison({ experimentId, segment }: Props) {
  const { t } = useTranslation('experiments');
  const { data, isLoading, isError } = useExperimentRoasComparison(experimentId, segment);
  // Fallback labels for an API that predates the per-arm split and sends no arm
  // letters — the fixed slot→letter mapping, same as the other Performance cards.
  const { label: armLabel } = useArmLabels(experimentId);

  /**
   * Resolve a wire arm letter to its display label, falling back to the raw
   * letter so an arm from a newer backend is shown rather than mislabelled.
   */
  const letterLabel = (letter: string): string => {
    const key = armLetterLabelKey(letter);
    return key ? t(key) : letter;
  };

  const controlHeader = data?.controlArm ? letterLabel(data.controlArm) : armLabel('control');
  // Column count keys off `test_arms`, never a null check on the value: a null
  // `testB` also means "arm exists but has no ad spend, so ROAS is undefined".
  const testArms = data?.testArms ?? [];
  const showSecondTestArm = testArms.length > 1;
  const testHeaders = [testArms[0] ? letterLabel(testArms[0]) : armLabel('testA')];
  if (showSecondTestArm && testArms[1]) testHeaders.push(letterLabel(testArms[1]));

  const columnCount = LEADING_COLUMNS + testHeaders.length * COLUMNS_PER_TEST_ARM;

  // Sources with no data are already dropped by the transform. One source needs
  // no in-table heading — it names the whole card, so it moves to the subtitle.
  const sections = data?.sections ?? [];
  const showSourceLabels = sections.length > 1;
  const singleSource = sections.length === 1 ? sections[0]?.sourceLabel : undefined;
  const subtitle = singleSource
    ? t('detail.roasComparison.subtitleSingle', { source: singleSource })
    : t('detail.roasComparison.subtitle');

  // Every source came back empty — nothing to frame, so show the same line as a
  // failed query rather than a header row over a blank grid.
  const isEmpty = !!data && sections.length === 0;

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="min-w-0 space-y-6 p-6">
        {/* Header */}
        <div>
          <h3 className="text-[16px] font-medium">{t('detail.roasComparison.title')}</h3>
          <p className="text-muted-foreground text-sm">{subtitle}</p>
        </div>

        {/* Table — collapses to a "data unavailable" line when the source
            query throws (no backend endpoint yet) or every source is empty. */}
        {isError || isEmpty ? (
          <ExperimentDataUnavailable />
        ) : (
          // One provider for the whole table rather than one per tooltip, so the
          // hover-delay context is shared across the ROAS rows.
          <TooltipProvider>
            <div className="overflow-x-auto">
              <table
                className={cn(
                  'w-full text-sm',
                  showSecondTestArm ? 'min-w-[880px]' : 'min-w-[640px]',
                )}
              >
                <caption className="sr-only">{t('detail.roasComparison.title')}</caption>
                <thead>
                  {/* Two header rows: each arm spans its value + lift columns, so
                    a lift is read under the arm it belongs to rather than under
                    a second identical "DID Lift" heading. */}
                  <tr>
                    <th rowSpan={2} className="w-[24%] pb-3 text-left font-normal" />
                    <th
                      rowSpan={2}
                      scope="col"
                      className="pb-3 text-left align-bottom font-semibold"
                    >
                      {controlHeader}
                    </th>
                    {testHeaders.map((header) => (
                      <th
                        key={header}
                        colSpan={COLUMNS_PER_TEST_ARM}
                        scope="colgroup"
                        className={cn('border-b pb-2 text-left font-semibold', GROUP_START)}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                  <tr className="border-b">
                    {testHeaders.map((header) => (
                      <Fragment key={header}>
                        <th
                          scope="col"
                          className={cn(
                            'text-muted-foreground pt-2 pb-3 text-left text-xs font-normal',
                            GROUP_START,
                          )}
                        >
                          {t('detail.roasComparison.value')}
                        </th>
                        <th
                          scope="col"
                          className="text-muted-foreground pt-2 pb-3 text-left text-xs font-normal"
                        >
                          {t('detail.roasComparison.didLift')}
                        </th>
                      </Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading || !data ? (
                    <RoasSkeletonRows columnCount={columnCount} armCount={testHeaders.length} />
                  ) : (
                    <RoasSectionRows
                      data={data}
                      showSecondTestArm={showSecondTestArm}
                      showSourceLabels={showSourceLabels}
                    />
                  )}
                </tbody>
              </table>
            </div>
          </TooltipProvider>
        )}

        {/* Footer — the cross-source caveat only applies once more than one
            source is on screen; the DID explanation always does. */}
        <p className="text-muted-foreground text-sm">
          {showSourceLabels && `${t('detail.roasComparison.footerMulti')} `}
          {t('detail.roasComparison.footer')}
        </p>
      </CardContent>
    </Card>
  );
}

/** Loading frame — two source sections of three metric rows, matching the real shape. */
function RoasSkeletonRows({ columnCount, armCount }: { columnCount: number; armCount: number }) {
  return (
    <>
      {Array.from({ length: 2 }).map((_, gi) => (
        <Fragment key={gi}>
          <tr>
            <td colSpan={columnCount} className={cn('px-0 pb-3', gi === 0 ? 'pt-5' : 'pt-9')}>
              <Skeleton className="mx-0 h-3 w-32" />
            </td>
          </tr>
          {Array.from({ length: 3 }).map((_, ri) => (
            <tr key={ri} className="border-b last:border-0">
              <td className="py-4">
                <Skeleton className="h-4 w-32" />
              </td>
              <td className="py-4">
                <Skeleton className="h-4 w-20" />
              </td>
              {Array.from({ length: armCount }).map((_, ai) => (
                <Fragment key={ai}>
                  <td className={cn('py-4', GROUP_START)}>
                    <Skeleton className="h-4 w-20" />
                  </td>
                  <td className="py-4">
                    <Skeleton className="h-4 w-20" />
                  </td>
                </Fragment>
              ))}
            </tr>
          ))}
        </Fragment>
      ))}
    </>
  );
}

/**
 * Source divider — an uppercase micro-label followed by a rule that fills the
 * remaining width. Reads as a separator between row groups rather than as a data
 * row, which a full-width filled band did. Rendered only when more than one
 * source has data; a single source names the card in its subtitle instead.
 */
function SourceLabelRow({
  label,
  columnCount,
  isFirst,
}: {
  label: string;
  columnCount: number;
  isFirst: boolean;
}) {
  return (
    <tr>
      <th
        colSpan={columnCount}
        scope="colgroup"
        className={cn('pb-3 text-left', isFirst ? 'pt-5' : 'pt-9')}
      >
        <span className="flex w-full items-center gap-3">
          <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            {label}
          </span>
          <span aria-hidden="true" className="bg-border h-px flex-1" />
        </span>
      </th>
    </tr>
  );
}

/**
 * Explains why one table shows two ROAS figures: ad spend is Google Ads' in both
 * sections, so the rows differ only by numerator. Rendered only alongside a
 * second source — with one section there is nothing to disambiguate.
 */
function RoasMetricTooltip() {
  const { t } = useTranslation('experiments');
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('detail.roasComparison.roasTooltipLabel')}
          className="text-muted-foreground hover:text-foreground size-4 cursor-pointer hover:bg-transparent"
        >
          <HelpCircle className="size-3.5" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">
        {t('detail.roasComparison.roasTooltip')}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The real rows — one group per source. Values arrive pre-formatted from the
 * transform (already an em dash where the wire sent null), and lift colour comes
 * from the wire's `*Direction` so a missing value is never painted green or red.
 */
function RoasSectionRows({
  data,
  showSecondTestArm,
  showSourceLabels,
}: {
  data: RoasComparisonResponse;
  showSecondTestArm: boolean;
  showSourceLabels: boolean;
}) {
  const columnCount = LEADING_COLUMNS + (showSecondTestArm ? 2 : 1) * COLUMNS_PER_TEST_ARM;

  return (
    <>
      {data.sections.map((section, si) => (
        <Fragment key={section.sourceLabel}>
          {showSourceLabels && (
            <SourceLabelRow
              label={section.sourceLabel}
              columnCount={columnCount}
              isFirst={si === 0}
            />
          )}
          {section.rows.map((row) => (
            <tr key={row.metric} className="border-b last:border-0">
              <th
                scope="row"
                className="text-muted-foreground py-4 text-left font-normal capitalize"
              >
                <span className="inline-flex items-center gap-1.5">
                  {row.metric}
                  {row.isRoas && showSourceLabels && <RoasMetricTooltip />}
                </span>
              </th>
              <td className="py-4">{row.control.replace('$', '')}</td>
              <td className={cn('py-4', GROUP_START)}>{row.testA.replace('$', '')}</td>
              <td className="py-4">
                <ExperimentLiftBadge value={row.didLift} direction={row.didLiftDirection} />
              </td>
              {showSecondTestArm && (
                <>
                  <td className={cn('py-4', GROUP_START)}>{row.testB.replace('$', '')}</td>
                  <td className="py-4">
                    <ExperimentLiftBadge
                      value={row.testBDidLift}
                      direction={row.testBDidLiftDirection}
                    />
                  </td>
                </>
              )}
            </tr>
          ))}
        </Fragment>
      ))}
    </>
  );
}
